import React, { FC } from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { TreatmentSegment } from "../../domain/treatmentPlan.interface";
import {
	activitySegmentsState,
	FOLLOWS_TEMPLATE_MARKER,
	OWN_SEGMENTS_MARKER,
} from "./segmentEditor.helpers";

/**
 * SIMS-9226 AC4 (FR-4; Hi-Fi v2 Screen 1, badge 15) — the pill on an Additional Activities row's
 * NAME line saying whether the activity follows its template's segments or holds its own, so the
 * template shows which activities override it without any card being opened. Never placed in the
 * row's "Segment / Duration / Visits/Period" caption (Q-1). It reads the form's activity entry, so
 * it flips as soon as DEFINE OWN SEGMENTS or FOLLOW TEMPLATE SEGMENTS is used.
 *
 * Same vocabulary and colour pair as the activity's block: dashed `link` / solid `link_off`.
 * Test id `activity-segments-marker-{index}` with `data-state` following | own. Synthetic data only.
 */
interface ActivitySegmentsMarkerProps {
	activityIndex: number;
	activity: { segments?: TreatmentSegment[] };
}

const ActivitySegmentsMarker: FC<ActivitySegmentsMarkerProps> = ({
	activityIndex,
	activity,
}) => {
	const state = activitySegmentsState(activity);
	const own = state === "own";
	const Icon = own ? LinkOffIcon : LinkIcon;
	return (
		<Box
			component="span"
			data-testid={`activity-segments-marker-${activityIndex}`}
			data-state={state}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.5,
				height: 20,
				px: 1,
				borderRadius: 10,
				fontSize: 12,
				fontWeight: 500,
				whiteSpace: "nowrap",
				...(own
					? {
							bgcolor: "primary.dark",
							color: "primary.contrastText",
							border: 1,
							borderColor: "primary.dark",
						}
					: {
							bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
							color: "primary.main",
							border: "1px dashed",
							borderColor: "primary.main",
						}),
			}}
		>
			<Icon sx={{ fontSize: 14 }} />
			{own ? OWN_SEGMENTS_MARKER : FOLLOWS_TEMPLATE_MARKER}
		</Box>
	);
};

export default ActivitySegmentsMarker;
